/**
 * Saha yapılandırması şemaları — SUNUCU VE ARAYÜZ AYNI TANIMI KULLANIR [K-020].
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * BU ŞEMALAR VERİ KALİTESİNİN İLK KAPISI.
 *
 * [K-011] gereği ETL yok: her saha buradan elle girilecek. Eski sistemde
 * yapılandırma denetimsizdi ve sonucu ölçüldü — 12 Ağustos analizinde 29 saha
 * 7 gündür veri vermiyordu, biri (`0.0.0.0`) panele IP hiç girilmediği için.
 * Kimse fark etmemişti.
 *
 * Buradaki her kısıt o sınıftan bir hatayı engellemek için var.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { z } from 'zod';
/* ── Ortak parçalar ─────────────────────────────────────────────────────── */
/**
 * IPv4 adresi — ve KULLANILAMAZ adreslerin reddi.
 *
 * `0.0.0.0` biçim olarak geçerli bir IPv4'tür; `z.string().ip()` onu kabul
 * eder. Ama bir PLC'ye bağlanmak için ANLAMSIZDIR: "adres yok" demektir.
 * Eski sistemde tam olarak bu değer bir panelde duruyordu ve 7 gün boyunca
 * 1.974 başarısız bağlantı denemesi üretti.
 *
 * Aynı gerekçeyle yayın (255.255.255.255) ve loopback de reddedilir —
 * sahadaki bir PLC bunların hiçbirinde olamaz.
 */
export const plcAdresi = z
    .string()
    .trim()
    .min(1, 'PLC adresi gerekli.')
    .regex(/^(\d{1,3}\.){3}\d{1,3}$/, 'Geçerli bir IPv4 adresi girin (örn. 188.38.115.12).')
    .refine((v) => v.split('.').every((p) => Number(p) <= 255), 'IPv4 bölümleri 0–255 arasında olmalı.')
    .refine((v) => v !== '0.0.0.0', 'PLC adresi 0.0.0.0 olamaz — bu "adres yok" demektir.')
    .refine((v) => v !== '255.255.255.255', 'Yayın adresi kullanılamaz.')
    .refine((v) => !v.startsWith('127.'), 'Loopback adresi (127.x) bir sahaya ait olamaz.');
/**
 * İSTEĞE BAĞLI adres — boş bırakılabilir.
 *
 * ─── FORMDAN ÇIKAN GERÇEK KUSUR (E adımı) ────────────────────────────────────
 * `plcAdresi.optional()` yalnız "alan hiç gönderilmedi" hâlini kabul ediyordu.
 * Ama form alanı boş bırakıldığında `''` gönderir — ve `''` "PLC adresi
 * gerekli" hatasına düşer. Sonuç ölçüldü: **genel ağ modundaki bir pano hiç
 * kaydedilemiyordu**, çünkü kullanılmayan tünel alanı boştu ve formu düşürüyordu.
 * Kullanıcı hatayı doldurmadığı bir alanda görürdü.
 *
 * `''` artık "değer yok" demek. `isteğeBağlıMetin`'in aynı kararı — orada
 * `null`'a, burada `undefined`'a çevriliyor: adres kolonları güncellemede
 * "dokunma" anlamını taşır.
 */
export const isteğeBağlıAdres = z.preprocess((v) => (v === '' || v === null ? undefined : v), plcAdresi.optional());
/** Enlem/boylam — Türkiye sınırlarını zorlamıyoruz ama dünya sınırlarını evet. */
export const enlem = z
    .number({ message: 'Enlem sayı olmalı.' })
    .min(-90, 'Enlem -90 ile 90 arasında olmalı.')
    .max(90, 'Enlem -90 ile 90 arasında olmalı.');
export const boylam = z
    .number({ message: 'Boylam sayı olmalı.' })
    .min(-180, 'Boylam -180 ile 180 arasında olmalı.')
    .max(180, 'Boylam -180 ile 180 arasında olmalı.');
const metin = (max, ad) => z.string().trim().min(1, `${ad} gerekli.`).max(max, `${ad} çok uzun.`);
/**
 * İsteğe bağlı metin — BOŞ DİZE `null`'a çevrilir, `undefined`'a DEĞİL.
 *
 * Fark düzenlemede ortaya çıkar ve önemlidir:
 *   · alan gövdede YOK (`undefined`) → "dokunma"
 *   · alan boş gönderildi (`null`)   → "TEMİZLE"
 *
 * İkisini de `undefined`'a indirseydik kullanıcı yanlış girdiği bir telefon
 * numarasını silemezdi: alanı boşaltıp kaydeder, eski değer geri gelirdi.
 */
const isteğeBağlıMetin = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === undefined ? undefined : v === '' || v === null ? null : v));
/* ── Saha ───────────────────────────────────────────────────────────────── */
/**
 * `firm_id` BU ŞEMADA YOKTUR — ve bu kasıtlıdır [K-010].
 *
 * Firma bağlamı oturumdan gelir; istemcinin gönderdiği hiçbir değer ona
 * karışamaz. Şemaya alsaydık sunucu tarafında "bunu yoksay" demeyi
 * hatırlamak gerekirdi — hatırlanmayan şeyler unutulur.
 */
export const siteCreateSchema = z.object({
    addrCode: metin(64, 'Tesisat/abone kodu'),
    /** Dış sistem saha kodu — `addr_code`'dan ayrı, isteğe bağlı (§5.2). */
    siteCode: isteğeBağlıMetin(64),
    name: metin(200, 'Saha adı'),
    city: isteğeBağlıMetin(80),
    district: isteğeBağlıMetin(80),
    address: isteğeBağlıMetin(500),
    latitude: enlem,
    longitude: boylam,
    contactPerson: isteğeBağlıMetin(120),
    contactPhone: isteğeBağlıMetin(40),
    contactEmail: z
        .union([
        z.literal(''),
        z.null(),
        z.string().trim().email('Geçerli bir e-posta girin.').max(320),
    ])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === '' || v === null ? null : v)),
    /** [K-001] müşteri İSTEĞE BAĞLI — müşterisiz saha (stok/depo) meşrudur. */
    customerId: z.number().int().positive().nullable().optional(),
});
/** Düzenleme: tüm alanlar isteğe bağlı, ama gönderilen alan aynı kuralı geçer. */
export const siteUpdateSchema = siteCreateSchema.partial();
/* ── Pano ───────────────────────────────────────────────────────────────── */
/**
 * Bağlantı modu ve adres — şemadaki `ck_panel_connection` kısıtının aynısı.
 *
 * Veritabanı zaten zorluyor; burada tekrarlanmasının sebebi kullanıcıya
 * ANINDA ve ANLAŞILIR geri bildirim vermek. Sunucuya gidip "check constraint
 * violation" almak kimseye yardımcı olmaz.
 */
const panelBase = z.object({
    siteId: z.number().int().positive({ message: 'Saha seçin.' }),
    serialNumber: metin(100, 'Seri numarası'),
    plcProfileId: z.number().int().positive({ message: 'PLC profili seçin.' }),
    connectionMode: z.enum(['public', 'vpn'], { message: 'Bağlantı türü seçin.' }),
    publicHost: isteğeBağlıAdres,
    tunnelIp: isteğeBağlıAdres,
    /**
     * PLC'nin SAHA İÇİNDEKİ adresi — erişim yolundan ayrıdır (şema §panels).
     *
     * İlk yazımda bu alan şemada YOKTU: form onu hiç sormaz, kolon NOT NULL
     * olduğu için veritabanı varsayılanı (192.168.1.10) sessizce yazardı. Sahada
     * PLC farklı bir adrestaysa pano "yapılandırıldı" görünür ama hiç okunamaz.
     * Varsayılan burada da aynı — fark, artık GÖRÜNÜR ve değiştirilebilir olması.
     */
    plcHost: plcAdresi.default('192.168.1.10'),
    plcPort: z
        .number()
        .int()
        .min(1, 'Port 1–65535 arasında olmalı.')
        .max(65535, 'Port 1–65535 arasında olmalı.')
        .default(502),
    /**
     * Yoklama aralığı — alt sınır 30 sn.
     *
     * Daha sıksı hem PLC'yi hem mobil kotayı zorlar (ölçüldü: poll başına
     * ~1 KB, ayda 9 MB @ 5 dk). Üst sınır 1 saat: daha seyreği "izleniyor"
     * sayılamaz.
     */
    pollIntervalSeconds: z
        .number()
        .int()
        .min(30, 'Yoklama aralığı en az 30 saniye olmalı.')
        .max(3600, 'Yoklama aralığı en fazla 1 saat olabilir.')
        .default(300),
    isPollingEnabled: z.boolean().default(true),
});
/**
 * Seçili moda ait adres dolu mu — `ck_panel_connection`'ın aynası.
 *
 * Oluşturma ve düzenleme AYNI fonksiyonu kullanır: kural iki yerde yazılırsa
 * biri güncellenir, diğeri unutulur.
 */
function bağlantıDenetle(v, ctx) {
    if (v.connectionMode === 'public' && v.publicHost === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['publicHost'],
            message: 'Genel erişimde PLC adresi zorunlu.',
        });
    }
    if (v.connectionMode === 'vpn' && v.tunnelIp === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['tunnelIp'],
            message: 'VPN modunda tünel adresi zorunlu.',
        });
    }
}
export const panelCreateSchema = panelBase.superRefine(bağlantıDenetle);
/**
 * Düzenleme — kontrol YALNIZ `connectionMode` gönderildiğinde çalışır.
 *
 * Sadece `pollIntervalSeconds` güncelleyen bir istek adres alanlarını hiç
 * göndermez; oradaki "adres zorunlu" kontrolü mevcut, kayıtlı adresi
 * görmediği için haksız yere reddederdi. Mod değişiyorsa yeni moda ait adres
 * aynı istekte gelmeli — bunu hem burada hem veritabanı kısıtı zorlar.
 */
export const panelUpdateSchema = panelBase.partial().superRefine(bağlantıDenetle);
/* ── Tank ───────────────────────────────────────────────────────────────── */
/**
 * Tank — SAHADA ya da DEPODA olabilir.
 *
 * `tanks` şemasındaki `ck_site_tank` kısıtı ikisini birlikte hareket ettirir:
 * saha varsa numara var, saha yoksa numara da yok. İlk yazımda bu şema sahayı
 * ZORUNLU tutuyordu — yani depodaki bir tank sisteme hiç girilemezdi ve
 * `site_id` nullable yapılmasının sebebi (henüz kurulmamış tank) kullanılamaz
 * kalırdı.
 */
const tankBase = z.object({
    /** NULL = depoda/stokta — henüz bir sahaya kurulmamış. */
    siteId: z.number().int().positive().nullish(),
    /**
     * Saha içi tank numarası 1–12.
     *
     * Üst sınır şemadaki `ck_register_scope` ile aynı. Eski sistem `tank1`–
     * `tank6` sabit sütunlarıyla altı tankla sınırlıydı; yeni sınır 12.
     */
    tankNumber: z
        .number()
        .int()
        .min(1, 'Tank numarası 1–12 arasında olmalı.')
        .max(12, 'Tank numarası 1–12 arasında olmalı.')
        .nullish(),
    serialNumber: metin(100, 'Tank seri numarası'),
    /** Kapasite `tank_capacities` tablosunda tanımlı olmalı — sunucu doğrular. */
    capacityM3: z.number().int().positive({ message: 'Kapasite seçin.' }),
    brand: isteğeBağlıMetin(80),
    productionYear: z
        .number()
        .int()
        .min(1950, 'Üretim yılı 1950 sonrası olmalı.')
        .max(2100)
        .nullish(),
    /*
     * Değerler `tank_orientation` enum'ıyla BİREBİR.
     *
     * İlk yazımda uydurma bir `underground` değeri vardı — şemada yok. Sunucu
     * onu reddederdi ama kullanıcı formda görüp seçebilirdi: "kaydet"e basana
     * kadar hatayı fark etmezdi. Enum'ı okumak yerine tahmin etmenin bedeli
     * tam olarak bu.
     */
    orientation: z.enum(['vertical', 'horizontal']).nullish(),
});
/**
 * `ck_site_tank`'in aynası — kullanıcı hatayı formda görsün, 500'de değil.
 *
 * ⚠️ Düzenlemede İKİSİ BİRLİKTE gönderilir. Yalnız `siteId` gönderip tankı
 * depoya çekmek, `tank_number`'ı eski değerinde bırakır ve kısıt patlar; bu
 * yüzden "biri varsa diğeri de var" kuralı burada da geçerlidir. Tankı depoya
 * almak = ikisini birden `null` göndermek.
 */
function yerleşimDenetle(v, ctx) {
    const sahaYok = v.siteId === undefined || v.siteId === null;
    const numaraYok = v.tankNumber === undefined || v.tankNumber === null;
    if (sahaYok !== numaraYok) {
        ctx.addIssue({
            code: 'custom',
            path: ['tankNumber'],
            message: 'Sahaya kurulu tankın numarası olmalı; depodaki tankın olmamalı.',
        });
    }
}
export const tankCreateSchema = tankBase.superRefine(yerleşimDenetle);
/**
 * Düzenleme — yerleşim kontrolü yalnız İKİSİNDEN BİRİ gönderildiğinde anlamlı.
 * Hiçbiri gönderilmediyse tankın yeri değişmiyor demektir; karışılmaz.
 */
export const tankUpdateSchema = tankBase.partial().superRefine((v, ctx) => {
    if (v.siteId === undefined && v.tankNumber === undefined)
        return;
    yerleşimDenetle(v, ctx);
});
//# sourceMappingURL=field.js.map